import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import {
  Phone,
  MessageCircle,
  CheckCircle2,
  CheckCircle,
  Circle,
  Clock,
  ArrowLeft,
  Navigation,
  Truck,
  Loader2,
  Shield,
  MapPin,
  X,
  Send,
  Receipt,
  AlertTriangle,
  Download,
  Package,
  ShoppingBag,
  CreditCard,
  Info,
} from 'lucide-react';
import { supabase, TowJob, Profile } from '../../lib/supabase';
import { JOB_STATUS_LABELS, formatKES, timeAgo } from '../../types';
import { RealMap } from '../../components/RealMap';
import { ChatMessage } from '../../components/ChatMessage';
import { useAuth } from '../../context/auth';
import jsPDF from 'jspdf';

const TIMELINE_STEPS: { key: string; label: string; icon: typeof Truck }[] = [
  { key: 'pending', label: 'Request received', icon: CheckCircle2 },
  { key: 'dispatched', label: 'Operator matched', icon: Truck },
  { key: 'en_route', label: 'Operator en route', icon: Truck },
  { key: 'arrived', label: 'Operator arrived', icon: Truck },
  { key: 'towing', label: 'Vehicle in tow', icon: Truck },
  { key: 'completed', label: 'Job completed', icon: CheckCircle2 },
];

const STATUS_ORDER = ['pending', 'dispatched', 'en_route', 'arrived', 'towing', 'completed'];

const STATUS_PROGRESS: Record<string, number> = {
  pending: 10,
  dispatched: 30,
  en_route: 50,
  arrived: 70,
  towing: 85,
  completed: 100,
  cancelled: 0,
};

// Simple reverse geocoding (in production, use Google Maps API or similar)
const getPlaceName = (address: string | null, coordinates?: string | null): string => {
  if (!address) return 'Unknown Location';
  if (address && address !== '') return address;
  if (coordinates) return coordinates;
  return 'Unknown Location';
};

// Calculate distance between two points (Haversine formula)
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// Calculate ETA based on distance and transport mode
function calculateETA(distanceKm: number, mode: 'car' | 'truck' | 'walking'): { minutes: number; text: string } {
  // Average speeds in Nairobi (considering traffic): car 40km/h, truck 35km/h, walking 5km/h
  const speeds = { car: 40, truck: 35, walking: 5 };
  const speed = speeds[mode];
  const hours = distanceKm / speed;
  const minutes = hours * 60;
  
  if (minutes < 1) return { minutes: 1, text: '< 1 min' };
  if (minutes < 60) return { minutes, text: `${minutes} min${minutes > 1 ? 's' : ''}` };
  const hoursVal = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return { minutes, text: `${hoursVal}h ${mins}m` };
};

export function CustomerTracking() {
  const { jobId } = useParams();
  const { profile } = useAuth();
  const [job, setJob] = useState<TowJob | null>(null);
  const [operator, setOperator] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [operatorLocation, setOperatorLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [customerLocation, setCustomerLocation] = useState<{ lat: number; lng: number } | null>(null);
  const location = useLocation();
  
  // Auto-open chat if URL has openChat parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('openChat') === 'true' && operator) {
      setShowChat(true);
    }
  }, [location.search, operator]);

  useEffect(() => {
    if (!jobId) return;
    let active = true;

    async function load() {
      const { data } = await supabase
        .from('tow_jobs')
        .select('*')
        .eq('id', jobId!)
        .maybeSingle();
      if (!active) return;
      setJob(data as TowJob | null);
      
      // Extract customer location from job coordinates
      if (data?.pickup_lat && data?.pickup_lng) {
        setCustomerLocation({ lat: data.pickup_lat, lng: data.pickup_lng });
      }
      
      if (data?.operator_id) {
        const { data: op } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.operator_id)
          .maybeSingle();
        if (active) setOperator(op as Profile | null);

        // Load operator location
        const { data: loc } = await supabase
          .from('operator_locations')
          .select('*')
          .eq('operator_id', data.operator_id)
          .maybeSingle();
        if (active && loc) {
          setOperatorLocation({ lat: loc.lat, lng: loc.lng });
        }
      }
      setLoading(false);
    }

    load();

    // Subscribe to job updates
    const jobChannel = supabase
      .channel(`job-${jobId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tow_jobs', filter: `id=eq.${jobId}` }, (payload) => {
        setJob(payload.new as TowJob);
        if ((payload.new as TowJob).operator_id && !operator) {
          supabase.from('profiles').select('*').eq('id', (payload.new as TowJob).operator_id).maybeSingle()
            .then(({ data }) => setOperator(data as Profile | null));
        }
      })
      .subscribe();

    // Subscribe to operator location updates
    let locationChannel: any = null;
    if (job?.operator_id) {
      locationChannel = supabase
        .channel(`operator-location-${job.operator_id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'operator_locations', filter: `operator_id=eq.${job.operator_id}` }, (payload) => {
          if (payload.new && typeof payload.new === 'object' && 'lat' in payload.new && 'lng' in payload.new) {
            setOperatorLocation({ lat: (payload.new as any).lat, lng: (payload.new as any).lng });
          }
        })
        .subscribe();
    }

    return () => {
      active = false;
      supabase.removeChannel(jobChannel);
      if (locationChannel) supabase.removeChannel(locationChannel);
    };
  }, [jobId]);
  
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="animate-spin text-primary-600" size={32} />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="card p-8 text-center">
        <p className="text-gray-500">Job not found.</p>
        <Link to="/" className="mt-3 inline-block font-semibold text-primary-600">Back to home</Link>
      </div>
    );
  }

  const currentStepIdx = STATUS_ORDER.indexOf(job.status);

  // Calculate real ETA based on distance and transport mode
  const calculateDynamicETA = () => {
    if (job.status === 'completed') return { eta: 'Completed', distance: '0 km', arrival: 'Service completed' };
    if (job.status === 'arrived') return { eta: 'Arrived', distance: '0 km', arrival: 'Here now' };
    if (job.status === 'towing') return { eta: 'En route', distance: '1.2 km', arrival: 'On the way' };

    // Calculate real distance if we have operator and customer locations
    if (operatorLocation && customerLocation) {
      const distance = calculateDistance(
        operatorLocation.lat, operatorLocation.lng,
        customerLocation.lat, customerLocation.lng
      );

      // Assume car/towing for operator - Nairobi traffic average 40km/h
      const eta = calculateETA(distance, 'car');
      const arrivalTime = new Date(Date.now() + eta.minutes * 60000).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit'
      });

      return {
        eta: eta.text,
        distance: `${distance.toFixed(1)} km`,
        arrival: `${arrivalTime}`
      };
    }

    // Fallback for en_route without location data
    if (job.status === 'en_route') return { eta: '8 Mins', distance: '2.8 km', arrival: 'Arriving soon' };
    if (job.status === 'dispatched') return { eta: '10 Mins', distance: '3.5 km', arrival: 'Preparing' };
    return { eta: 'Finding operator', distance: '--', arrival: 'Searching' };
  };
  
  const etaData = calculateDynamicETA();

  // Check if this is a parts request (no map tracking needed)
  // Only 'parts' requests don't show map tracking
  const isPartsRequest = job.service_type === 'parts';

  // Function to download receipt
  const downloadReceipt = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Header with branding
    doc.setFillColor(220, 38, 38);
    doc.rect(0, 0, pageWidth, 50, 'F');

    // Logo placeholder
    doc.setFillColor(255, 255, 255);
    doc.circle(30, 25, 12, 'F');
    doc.setTextColor(220, 38, 38);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Mk', 30, 29, { align: 'center' });

    // Company name and tagline
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('Mkononi', 50, 22);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('One breakdown. One connected journey.', 50, 30);

    // Receipt title
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('OFFICIAL RECEIPT', pageWidth - 20, 35, { align: 'right' });

    // Divider line
    doc.setDrawColor(220, 38, 38);
    doc.setLineWidth(2);
    doc.line(20, 55, pageWidth - 20, 55);

    // Receipt number and date
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Receipt #: ${job.reference}`, 20, 65);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, pageWidth - 20, 65, { align: 'right' });

    // Customer info section
    doc.setFillColor(245, 245, 245);
    doc.roundedRect(20, 75, pageWidth - 40, 30, 3, 3, 'F');

    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38);
    doc.text('CUSTOMER', 25, 85);

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(profile?.full_name || 'Customer', 25, 93);
    doc.text(profile?.phone || '', 25, 100);

    // Service details section
    let y = 120;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38);
    doc.text('SERVICE DETAILS', 20, y);
    y += 10;

    // Table header
    doc.setFillColor(220, 38, 38);
    doc.rect(20, y, pageWidth - 40, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('DESCRIPTION', 25, y + 5);
    doc.text('AMOUNT', pageWidth - 40, y + 5, { align: 'right' });

    y += 8;
    doc.setDrawColor(200, 200, 200);
    doc.line(20, y, pageWidth - 20, y);

    // Service row
    y += 5;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(job.service_type.replace('_', ' ').toUpperCase(), 25, y + 5);
    doc.text(formatKES(Number(job.total_amount)), pageWidth - 40, y + 5, { align: 'right' });

    y += 10;
    doc.setDrawColor(200, 200, 200);
    doc.line(20, y, pageWidth - 20, y);

    // Total
    y += 8;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38);
    doc.text('TOTAL', 25, y + 5);
    doc.text(formatKES(Number(job.total_amount)), pageWidth - 40, y + 5, { align: 'right' });

    // Payment method
    y += 20;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text(`Payment Method: ${job.payment_method.toUpperCase()}`, 20, y);
    y += 6;
    doc.text(`Payment Status: PAID`, 20, y);
    if (job.paid_at) {
      y += 6;
      doc.text(`Paid At: ${new Date(job.paid_at).toLocaleString()}`, 20, y);
    }
    if (operator?.full_name) {
      y += 6;
      doc.text(`Confirmed By: ${operator.full_name}`, 20, y);
    }

    // Locations section
    y += 20;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38);
    doc.text('LOCATIONS', 20, y);
    y += 10;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    doc.text('Pickup Location:', 20, y);
    y += 5;
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(job.pickup_address, 20, y);
    y += 8;

    if (job.destination_place_name) {
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      doc.text('Destination:', 20, y);
      y += 5;
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(job.destination_place_name, 20, y);
      y += 8;
    }

    // Issue description
    if (job.issue_description) {
      y += 10;
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(220, 38, 38);
      doc.text('SERVICE NOTES', 20, y);
      y += 10;

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 60, 60);
      const descLines = doc.splitTextToSize(job.issue_description, pageWidth - 40);
      doc.text(descLines, 20, y);
    }

    // Service provider
    y = pageHeight - 60;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(220, 38, 38);
    doc.text('SERVICE PROVIDER', 20, y);
    y += 8;

    if (operator) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(operator.full_name, 20, y);
      y += 5;
      doc.text(operator.business_name || 'Tower', 20, y);
      if (operator.truck_plate) {
        y += 5;
        doc.text(`Vehicle: ${operator.truck_plate}`, 20, y);
      }
    }

    // Footer
    y = pageHeight - 25;
    doc.setDrawColor(220, 38, 38);
    doc.line(20, y, pageWidth - 20, y);
    y += 8;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(128, 128, 128);
    doc.text('Thank you for choosing Mkononi. Your satisfaction is our priority.', pageWidth / 2, y, { align: 'center' });
    doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, y + 5, { align: 'center' });
    doc.text('This is an official receipt. For inquiries, contact support@mkononi.co.ke', pageWidth / 2, y + 10, { align: 'center' });

    doc.save(`Mkononi_Receipt_${job.reference}.pdf`);
  };

  // If job is completed or payment_completed, show completed summary instead of tracking
  if (job.status === 'completed' || job.status === 'payment_completed') {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderBottom: '1px solid var(--border)',
          padding: '1rem 1.5rem',
          position: 'sticky',
          top: 0,
          zIndex: 50
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1200px', margin: '0 auto' }}>
            <Link to="/" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: '#fef2f2',
              border: '1px solid #dc2626',
              borderRadius: '0.375rem',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
              color: '#dc2626',
              textDecoration: 'none',
              fontWeight: 600,
              transition: 'all 0.2s ease'
            }}>
              <ArrowLeft size={16} />
              <span>Back to Dashboard</span>
            </Link>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <CheckCircle2 size={20} style={{ color: 'var(--green)' }} />
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--green)' }}>
                {job.status === 'payment_completed' ? 'Payment Completed' : 'Job Completed'}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '1.5rem' }}>
          {/* Job Details Card */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border)' }}>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.5rem' }}>
                Job Details
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>Reference:</span>
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)' }}>{job.reference}</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Service Type</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)' }}>{job.service_type.replace('_', ' ')}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Total Amount</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#dc2626' }}>{formatKES(Number(job.total_amount))}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Payment Method</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)' }}>{job.payment_method.toUpperCase()}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Completed Date</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)' }}>{new Date(job.completed_at || job.updated_at).toLocaleDateString()}</div>
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Pickup Location</div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{job.pickup_address}</div>
            </div>

            {job.destination_place_name && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Destination</div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{job.destination_place_name}</div>
              </div>
            )}

            {job.issue_description && (
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Issue Description</div>
                <div style={{ fontSize: '1rem', color: 'var(--text)' }}>{job.issue_description}</div>
              </div>
            )}
          </div>

          {/* Tower/Garage Info */}
          {operator && (
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>
                Service Provider
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.5rem',
                  fontWeight: 700
                }}>
                  {operator.full_name.charAt(0)}
                </div>
                <div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.25rem' }}>
                    {operator.full_name}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                    {operator.business_name || 'Tower'} {operator.truck_plate && ` • ${operator.truck_plate}`}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Download Receipt Button - Only for paid jobs */}
          {job.status === 'payment_completed' && (
            <button
              onClick={downloadReceipt}
              style={{
                width: '100%',
                padding: '1rem',
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                marginBottom: '1.5rem'
              }}
            >
              <Download size={20} />
              Download Receipt
            </button>
          )}

          {/* Payment Status for unpaid completed jobs */}
          {job.status === 'completed' && job.status !== 'payment_completed' && (
            <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)', border: '2px solid #f97316' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: '#f97316',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Info size={24} color="white" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.25rem' }}>
                    Payment Required
                  </h3>
                  <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                    Please pay the tower directly. They will confirm payment and issue your receipt.
                  </p>
                </div>
              </div>
              <div style={{ fontSize: '0.85rem', color: '#1e40af', lineHeight: 1.5 }}>
                <div style={{ marginBottom: '0.25rem' }}><strong>Amount:</strong> {formatKES(Number(job.total_amount))}</div>
                <div><strong>Pay To:</strong> {operator?.full_name || 'Tower'}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // If this is a parts request, show order tracking instead of map
  if (isPartsRequest) {
    return (
      <>
      <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderBottom: '1px solid var(--border)',
          padding: '1rem 1.5rem',
          position: 'sticky',
          top: 0,
          zIndex: 50
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1200px', margin: '0 auto' }}>
            <Link to="/" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: '#fef2f2',
              border: '1px solid #dc2626',
              borderRadius: '0.375rem',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
              fontSize: '0.875rem',
              color: '#dc2626',
              textDecoration: 'none',
              fontWeight: 600,
              transition: 'all 0.2s ease'
            }}>
              <ArrowLeft size={16} />
              <span>Back to Dashboard</span>
            </Link>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Package size={20} style={{ color: '#dc2626' }} />
              <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#dc2626' }}>Parts Order</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '1.5rem' }}>
          {/* Reference Badge */}
          <div className="card" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '0.25rem' }}>ORDER REFERENCE</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{job.reference}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '0.25rem' }}>STATUS</div>
              <span className="badge badge-blue">{job.status.replace('_', ' ').toUpperCase()}</span>
            </div>
          </div>

          {/* Order Status Timeline */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1.5rem' }}>
              Order Status
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
              {[
                { key: 'pending', label: 'Order Placed', icon: ShoppingBag },
                { key: 'dispatched', label: 'Processing', icon: Package },
                { key: 'en_route', label: 'Shipped', icon: Truck },
                { key: 'arrived', label: 'Out for Delivery', icon: MapPin },
                { key: 'completed', label: 'Delivered', icon: CheckCircle2 },
              ].map((step, idx) => {
                const stepIdx = ['pending', 'dispatched', 'en_route', 'arrived', 'completed'].indexOf(step.key);
                const currentIdx = ['pending', 'dispatched', 'en_route', 'arrived', 'completed'].indexOf(job.status);
                const isCompleted = stepIdx <= currentIdx;
                const isCurrent = step.key === job.status;

                return (
                  <div key={step.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: '0.5rem' }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: isCompleted ? '#dc2626' : '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: isCompleted ? 'white' : '#9ca3af',
                      position: 'relative'
                    }}>
                      {isCompleted ? <CheckCircle2 size={16} /> : <step.icon size={16} />}
                      {isCurrent && (
                        <div style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-4px',
                          width: '12px',
                          height: '12px',
                          background: '#dc2626',
                          borderRadius: '50%',
                          border: '2px solid white'
                        }} />
                      )}
                    </div>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: isCompleted ? '#dc2626' : 'var(--muted)', textAlign: 'center' }}>
                      {step.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Order Details */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>
              Order Details
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Part Type</div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{job.service_type.replace('_', ' ')}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Total Amount</div>
                <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>{formatKES(Number(job.total_amount))}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Payment Method</div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{job.payment_method.toUpperCase()}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Created</div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{timeAgo(job.created_at)}</div>
              </div>
            </div>

            {job.issue_description && (
              <div style={{ marginTop: '1rem' }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>Description</div>
                <div style={{ fontSize: '1rem', color: 'var(--text)' }}>{job.issue_description}</div>
              </div>
            )}
          </div>

          {/* Vendor Info */}
          {operator && (
            <div className="card" style={{ marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>
                Parts Vendor
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.5rem',
                  fontWeight: 700
                }}>
                  {operator.full_name.charAt(0)}
                </div>
                <div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.25rem' }}>
                    {operator.full_name}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>
                    {operator.business_name || 'Parts Vendor'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Contact Vendor */}
          <div className="card">
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>
              Contact Vendor
            </h2>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                onClick={() => window.open(`tel:${operator?.phone}`)}
              >
                <Phone size={18} />
                Call Vendor
              </button>
              <button
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'white',
                  color: '#dc2626',
                  border: '2px solid #dc2626',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                onClick={() => setShowChat(true)}
              >
                <MessageCircle size={18} />
                Message Vendor
              </button>
            </div>
          </div>
        </div>

        {/* Chat Modal */}
        {showChat && (
          <div style={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: '400px',
            height: '100%',
            background: 'white',
            boxShadow: '-4px 0 16px rgba(0,0,0,0.1)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              padding: '1rem',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ fontWeight: 700, color: 'var(--text)' }}>Chat with Vendor</div>
              <button
                onClick={() => setShowChat(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ flex: 1, padding: '1rem', overflow: 'auto' }}>
              <div style={{ textAlign: 'center', color: 'var(--muted)', marginTop: '2rem' }}>
                Chat feature coming soon
              </div>
            </div>
          </div>
        )}
      </div>
      </>
    );
  }

  // Service request tracking with live map
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid var(--border)',
        padding: '1rem 1.5rem',
        position: 'sticky',
        top: 0,
        zIndex: 50
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', maxWidth: '1200px', margin: '0 auto' }}>
          <Link to="/" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: '#fef2f2',
            border: '1px solid #dc2626',
            borderRadius: '0.375rem',
            padding: '0.5rem 1rem',
            cursor: 'pointer',
            fontSize: '0.875rem',
            color: '#dc2626',
            textDecoration: 'none',
            fontWeight: 600,
            transition: 'all 0.2s ease'
          }}>
            <ArrowLeft size={16} />
            <span>Back to Dashboard</span>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ width: '8px', height: '8px', background: 'var(--green)', borderRadius: '50%', animation: 'pulse 2s infinite' }}></span>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--green)' }}>Live Tracking</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '1.5rem' }}>
        {/* Reference Badge */}
        <div className="card" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '0.25rem' }}>JOB REFERENCE</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text)' }}>{job.reference}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--muted)', fontWeight: 600, marginBottom: '0.25rem' }}>STATUS</div>
            <span className="badge badge-blue">{job.status.replace('_', ' ').toUpperCase()}</span>
          </div>
        </div>

        {/* Live Map */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div style={{ height: '400px', borderRadius: '8px', overflow: 'hidden' }}>
            {customerLocation && (
              <RealMap
                customerLocation={customerLocation}
                operatorLocation={operatorLocation || undefined}
                destinationLocation={
                  job.destination_lat && job.destination_lng
                    ? { lat: job.destination_lat, lng: job.destination_lng }
                    : undefined
                }
              />
            )}
          </div>
        </div>

        {/* ETA Banner */}
        <div style={{
          background: 'linear-gradient(135deg, #fecaca 0%, #fee2e2 100%)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <div style={{ fontSize: '0.875rem', color: '#7f1d1d', marginBottom: '0.25rem', fontWeight: 600 }}>
              ARRIVING IN
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 800, color: '#dc2626' }}>
              {etaData.eta}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#7f1d1d' }}>
              {etaData.distance} away
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.875rem', color: '#7f1d1d', marginBottom: '0.25rem', fontWeight: 600 }}>
              ETA
            </div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>
              {etaData.arrival}
            </div>
          </div>
        </div>

        {/* Progress */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 600 }}>Progress</span>
            <span style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>{STATUS_PROGRESS[job.status] || 0}%</span>
          </div>
          <div style={{ height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              background: 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)',
              width: `${STATUS_PROGRESS[job.status] || 0}%`,
              borderRadius: '4px',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Operator Profile */}
        {operator && (
          <div className="card" style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '2rem',
              fontWeight: 700,
              animation: 'pulse 2s infinite'
            }}>
              {operator.full_name.charAt(0)}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.25rem' }}>
                {operator.full_name}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                {operator.ntsa_certified && (
                  <span style={{
                    background: '#d1fae5',
                    color: '#059669',
                    padding: '0.125rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }}>
                    NTSA Certified
                  </span>
                )}
              </div>
              <div style={{ fontSize: '0.875rem', color: 'var(--muted)' }}>
                {operator.truck_plate || 'KDA 100A'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                style={{
                  padding: '0.5rem 1rem',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onClick={() => window.open(`tel:${operator.phone}`)}
              >
                <Phone size={16} />
                Call
              </button>
              <button
                style={{
                  padding: '0.5rem 1rem',
                  background: 'white',
                  color: '#dc2626',
                  border: '2px solid #dc2626',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onClick={() => setShowChat(true)}
              >
                <MessageCircle size={16} />
                Chat
              </button>
            </div>
          </div>
        )}

        {/* Payment Info - Show when job is completed but not paid */}
        {job.status === 'completed' && job.status !== 'payment_completed' && (
          <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)', border: '2px solid #f97316' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: '#f97316',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Info size={28} color="white" />
              </div>
              <div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.25rem' }}>
                  Payment Required
                </h3>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                  Please complete payment to receive your official receipt
                </p>
              </div>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '8px',
              padding: '1.25rem',
              marginBottom: '1rem',
              border: '1px solid #fed7aa'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#92400e', marginBottom: '0.25rem', fontWeight: 600 }}>
                    TOTAL AMOUNT
                  </div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#ea580c' }}>
                    {formatKES(Number(job.total_amount))}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#92400e', marginBottom: '0.25rem', fontWeight: 600 }}>
                    PAY TO
                  </div>
                  <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1f2937' }}>
                    {operator?.full_name || 'Tower'}
                  </div>
                  {operator?.phone && (
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      {operator.phone}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div style={{ fontSize: '0.85rem', color: '#92400e', lineHeight: 1.6, background: '#fffbeb', padding: '0.75rem', borderRadius: '6px' }}>
              <strong style={{ display: 'block', marginBottom: '0.25rem' }}>Payment Instructions:</strong>
              Pay the tower directly using your preferred method (Cash, M-Pesa, or Bank Transfer). Once payment is received, the tower will confirm and your official receipt will be available for download.
            </div>
          </div>
        )}

        {/* Payment Success - Show when tower has confirmed payment */}
        {job.status === 'payment_completed' && (
          <div className="card" style={{ marginBottom: '1.5rem', background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', border: '2px solid #10b981' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: '#10b981',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckCircle2 size={24} color="white" />
              </div>
              <div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.25rem' }}>
                  Payment Completed
                </h3>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>
                  Your payment has been confirmed. You can now download your receipt.
                </p>
              </div>
            </div>
            <div style={{ fontSize: '0.85rem', color: '#065f46', lineHeight: 1.5 }}>
              <div style={{ marginBottom: '0.25rem' }}><strong>Amount:</strong> {formatKES(Number(job.total_amount))}</div>
              <div><strong>Payment Method:</strong> {job.payment_method?.toUpperCase() || 'CASH'}</div>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1.5rem' }}>
            Tracking Progress
          </h2>
          <div className="tracking-timeline" style={{ display: 'flex', flexDirection: 'row', gap: '1rem', flexWrap: 'nowrap' }}>
            {TIMELINE_STEPS.map((step, idx) => {
              const isCompleted = idx <= currentStepIdx;
              const isCurrent = idx === currentStepIdx;

              return (
                <div key={step.key} className="tracking-timeline-item" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 'fit-content' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: isCompleted ? '#dc2626' : '#e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isCompleted ? 'white' : '#9ca3af',
                    flexShrink: 0
                  }}>
                    <step.icon size={16} />
                  </div>
                  <div style={{ minWidth: '120px' }}>
                    <div style={{ fontWeight: 600, color: isCompleted ? '#dc2626' : 'var(--text)', fontSize: '0.9rem' }}>
                      {step.label}
                    </div>
                    {isCurrent && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                        In progress
                      </div>
                    )}
                  </div>
                  {idx < TIMELINE_STEPS.length - 1 && (
                    <div className="timeline-connector" style={{
                      width: '40px',
                      height: '2px',
                      background: isCompleted ? '#dc2626' : '#e5e7eb',
                      flexShrink: 0
                    }} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Job Details */}
        <div className="card">
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>
            Job Details
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>SERVICE TYPE</div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{job.service_type.replace('_', ' ')}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>TOTAL AMOUNT</div>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#dc2626' }}>{formatKES(Number(job.total_amount))}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>PAYMENT METHOD</div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{job.payment_method}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>CREATED</div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>{timeAgo(job.created_at)}</div>
            </div>
          </div>

          <div style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>PICKUP LOCATION</div>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: '#dc2626' }}>{job.pickup_address}</div>
          </div>

          {job.destination_place_name && (
            <div style={{ marginBottom: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>DESTINATION</div>
              <div style={{ fontSize: '1rem', fontWeight: 600, color: '#059669' }}>{job.destination_place_name}</div>
            </div>
          )}

          {job.issue_description && (
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>ISSUE DESCRIPTION</div>
              <div style={{ fontSize: '1rem', color: 'var(--text)' }}>{job.issue_description}</div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--text)', marginBottom: '1rem' }}>
            Quick Actions
          </h2>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {job.status === 'payment_completed' && (
              <button
                style={{
                  flex: 1,
                  minWidth: '140px',
                  padding: '0.75rem',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
                onClick={downloadReceipt}
              >
                <Download size={18} />
                Download Receipt
              </button>
            )}
            <button
              style={{
                flex: 1,
                padding: '0.75rem',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onClick={() => setShowChat(true)}
            >
              <MessageCircle size={18} />
              Chat
            </button>
            <button
              style={{
                flex: 1,
                padding: '0.75rem',
                background: 'white',
                color: '#dc2626',
                border: '2px solid #dc2626',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
              onClick={() => window.open(`tel:${operator?.phone}`)}
            >
              <Phone size={18} />
              Call
            </button>
          </div>
        </div>
      </div>

      {/* Chat Modal */}
      {showChat && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '400px',
          height: '100%',
          background: 'white',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.1)',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{
            padding: '1rem',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ fontWeight: 700, color: 'var(--text)' }}>Chat with Operator</div>
            <button
              onClick={() => setShowChat(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem' }}
            >
              <X size={20} />
            </button>
          </div>
          <div style={{ flex: 1, padding: '1rem', overflow: 'auto' }}>
            <div style={{ textAlign: 'center', color: 'var(--muted)', marginTop: '2rem' }}>
              <MessageCircle size={48} style={{ opacity: 0.5, marginBottom: '1rem' }} />
              <p>Chat with your operator about this job</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
